import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AgentFunctions from "../../../../API/AgentFunctions";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import Utility from "../../../../Utility";
import { isLargeScreen } from "../../../MainViewContainer";
import FunctionSettingsAIView from "../SubComponents/FunctionSettingsAIView";
import validations from "./validations";
import Agents from "../../../../API/Agents";
import semverHelper from "../../../../Utils/semverHelper";

const FunctionSettingAI = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onFunctionCreated,
      selectedId,
      setShowFunctionModal,
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
      onDeleted,
    },
    ref,
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();

    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace, loadAgents } = useWorkspaces();
    const { user } = AppUser.useUser();
    const propertySchema = AppForms.yup.object().shape({
      type: AppForms.yup
        .string()
        .oneOf(["string"], 'Type must be "string"')
        .required("Type is required"),
      enum: AppForms.yup.array().of(AppForms.yup.string()).notRequired(),
      description: AppForms.yup.string().required("Description is required"),
    });
    const validationSchema = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(512),
      requestType: AppForms.yup.string().required().label().max(30),
      authorisationType: AppForms.yup.string().required().label().max(50),
      url: AppForms.yup
        .string()
        .required()
        .label()
        .max(255)
        .test(
          "is-valid-url-with-templates",
          "Must be a valid URL with optional {{variables}} for dynamic injection of path params",
          (value) => {
            if (!value) return false;

            // Create a version with placeholders removed for URL validation
            const testUrl = value.replace(/\{\{[^}]+\}\}/g, "placeholder");

            try {
              new URL(testUrl);
              return true;
            } catch (e) {
              return false;
            }
          },
        )
        .nullable(),
      parameters: validations.parametersVal,
      model: AppForms.yup
        .string()
        .nullable()
        .label()
        .max(255)
        .notOneOf(["None"]),
      // validate model is not None
      headers: AppForms.yup.object().nullable()?.notRequired(),
      staticArgs: AppForms.yup.object().nullable()?.notRequired(),
      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
      paramsAsPath: AppForms.yup.boolean().nullable()?.notRequired(),
      isDatabaseScript: AppForms.yup.boolean().nullable()?.notRequired(),
      scope: AppForms.yup.string().nullable()?.notRequired(),
      graphqlQuery: AppForms.yup
        .string()
        .nullable()
        .label()
        .max(8192)
        .test(
          "starts-with-query",
          'GraphQL query must start with "query" keyword (e.g., query GetData($id: ID!) { ... })',
          (value) => {
            if (!value) return true;
            const trimmed = value.trim().toLowerCase();
            return trimmed.startsWith("query");
          },
        ),
      graphqlMutation: AppForms.yup
        .string()
        .nullable()
        .label()
        .max(8192)
        .test(
          "starts-with-mutation",
          'GraphQL mutation must start with "mutation" keyword (e.g., mutation UpdateData($id: ID!) { ... })',
          (value) => {
            if (!value) return true;
            const trimmed = value.trim().toLowerCase();
            return trimmed.startsWith("mutation");
          },
        ),
      graphqlOperationName: AppForms.yup.string().nullable().label().max(255),
      functionVersion: AppForms.yup
        .string()
        .label()
        .nullable()
        .matches(/^\d+\.\d+\.\d+$/, "Use format 1.2.3"),
    });

    const validationSchemaDB = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(255),
      dbScript: AppForms.yup
        .string()
        .required()
        .label("Stored procedure")
        .max(255)
        .nullable(),
      parameters: validations.parametersVal,
      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
      isDatabaseScript: AppForms.yup.boolean().nullable()?.notRequired(),
      scope: AppForms.yup.string().nullable()?.notRequired(),
    });
    const validationSchemaHtml = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(255),
      htmlUrl: AppForms.yup
        .string()
        // .url("Must be a valid URL")
        .required()
        .label("URL")
        .max(255)
        .nullable(),
      htmlFormOnSuccessfulSubmitMessage: AppForms.yup
        .string()
        .label("Success message")
        .max(255)
        .nullable(),
      htmlFormOnFailedSubmitMessage: AppForms.yup
        .string()

        .label("Failure message")
        .max(255)
        .nullable(),
      isHtmlRedirectFn: AppForms.yup.boolean().nullable()?.notRequired(),
      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
      isHtmlFunctionsEnabled: AppForms.yup.boolean().nullable()?.notRequired(),
    });
    const validationSchemaChat = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(255),
      chatScript: AppForms.yup
        .string()
        // .url("Must be a valid URL")
        .required()
        .label("Script")
        .max(2048)
        .nullable(),
      chatAction: AppForms.yup
        .string()
        // .url("Must be a valid URL")
        .required()
        .label("Action")
        .max(255)
        .nullable(),

      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
    });
    const validationSchemaCode = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(512),
      parameters: validations.parametersVal,
      codeExecutionEnvironmentID: AppForms.yup
        .string()
        // .url("Must be a valid URL")
        .required()
        .label("Code execution hook")
        .nullable(),

      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
      handedOffToHumanOnCall: AppForms.yup.boolean().nullable()?.notRequired(),
    });

    const validationSchemaMCP = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(512),
      url: AppForms.yup
        .string()
        .required()
        .label("MCP Server URL")
        .max(255)
        .nullable(),
      parameters: AppForms.yup.object().nullable().notRequired(),
      authorisationType: AppForms.yup.string().required().label().max(50),
      headers: AppForms.yup.object().nullable()?.notRequired(),
      staticArgs: AppForms.yup.object().nullable()?.notRequired(),
      isCallbackFn: AppForms.yup.boolean().nullable()?.notRequired(),
      isMCPServer: AppForms.yup.boolean().nullable()?.notRequired(),
      scope: AppForms.yup.string().nullable()?.notRequired(),
    });

    const validationSchemaAgentSkill = AppForms.yup.object().shape({
      name: AppForms.yup
        .string()
        .required()
        .label()
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces.",
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup.string().required().label().max(512),
      agentSkillScript: AppForms.yup
        .string()
        .required()
        .label("Agent Skill Script")
        .max(8192)
        .nullable(),
      isAgentSkill: AppForms.yup.boolean().nullable()?.notRequired(),
      scope: AppForms.yup.string().nullable()?.notRequired(),
    });

    const handleDeletion = async () => {
      try {
        for (let i = 0; i < state?.agents?.length; i++) {
          if (state?.agents[i]?.agentFunctions?.includes(initialMapValues.id)) {
            const newFunctions = state?.agents[i]?.agentFunctions?.filter(
              (u) => u !== initialMapValues.id,
            );
            const newAgent = await Agents.updateAgent(
              state?.agents[i]?.id,
              [
                {
                  property: "agentFunctions",
                  value: newFunctions,
                },
              ],
              user?.id,
            );
            setTimeout(() => {}, 500);
          }
        }
        await loadAgents(getActiveWorkspace()?.id);

        const result = await AgentFunctions.deleteAgentFunction(
          initialMapValues.id,
        );
        dispatch("SET_AGENTS_FUNCTIONS", [
          ...state.agentsFunctions?.filter(
            (u) => u?.id !== initialMapValues.id,
          ),
        ]);
        setShowFunctionModal(false);
        AppToasty.show(
          `${Utility.upperCaseFirstLetter("dictionary")} ${t("deleted")}`,
          {
            type: "success",
          },
        );
        onDeleted && onDeleted();
      } catch (error) {
        console.error("dictionaryDeleteErrorCaption", error);
        AppToasty.show(
          `${
            t("dictionaryDeleteErrorCaption") + " "
          } ${Utility.upperCaseFirstLetter("dictionary")}`,
          {
            type: "danger",
          },
        );
      }
    };
    const navigateBack = () => {
      setDisplayedView(backTo);
    };
    const identifyOptByID = (id) => {
      return state.agentsFunctions.find((u) => u?.id === id);
    };
    const handleFunctionItemSave = async (submitted) => {
      let v = { ...submitted };
      v.model = "sorcerer";
      if (v?.scope == "none") v.scope = null;
      v.workspaceID = getActiveWorkspace()?.id;
      v.authorisationType = v.authorisationType?.toLowerCase();
      v.requestType = v.requestType?.toLowerCase();

      v.parameters = JSON.stringify(v?.parameters || {});
      v.headers = JSON.stringify(v?.headers || {});
      v.staticArgs = JSON.stringify(v?.staticArgs || {});
      v.usersToHandOffTo = v?.usersToHandOffTo
        ?.map((u) => u?.id)
        ?.filter((u) => u);
      v.functionVersion =
        semverHelper.normalizeVersion(v?.functionVersion) ||
        (isEdit ? null : "0.1.0");
      delete v?._lastChangedAt;
      delete v?.createdAt;
      delete v?.updatedAt;
      delete v?._deleted;
      delete v?.__typename;
      let newFunctions = [...state.agentsFunctions];

      if (v?.isCodeTemplate && v?.codeExecutionEnvironmentID == null)
        AppToasty.show(t("toasts.selectExecutionHook"), {
          type: "error",
          placement: "bottom",
        });

      // Agent skills don't need these fields
      if (v?.isAgentSkill) {
        v.authorisationType = null;
        v.requestType = null;
        v.parameters = null;
        v.headers = null;
        v.staticArgs = null;
        v.graphqlQuery = null;
        v.graphqlMutation = null;
        v.graphqlOperationName = null;
      }
      try {
        let result;
        if (isEdit) {
          result = await AgentFunctions.updateAgentFunction(
            {
              id: selectedId,
              ...v,
            },
            identifyOptByID(selectedId)?._version,
            user?.id,
          );
          const _updatedObject = identifyOptByID(
            result.data?.updateAgentFunction?.id,
          );
          const updateIndex = newFunctions.findIndex(
            (u) => u?.id === _updatedObject?.id,
          );
          newFunctions[updateIndex] = result?.data?.updateAgentFunction;
        } else {
          result = await AgentFunctions.createAgentFunction(
            {
              ...v,
            },
            user?.id,
          );

          newFunctions.push(result?.data?.createAgentFunction);
        }

        dispatch("SET_AGENTS_FUNCTIONS", [...newFunctions]);
        backTo && navigateBack();
        onFunctionCreated &&
          onFunctionCreated(
            "success",
            result?.data?.updateAgentFunction?.id ||
              result?.data?.createAgentFunction?.id ||
              selectedId,
            submitted,
          );
      } catch (error) {
        console.error("[TOOL_SAVE] API failed", error);
        onFunctionCreated && onFunctionCreated("error");
      } finally {
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  width: "100%",
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingHorizontal: 24,
                  paddingBottom: 40,
                  paddingTop: 12,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  // height: 500,
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Function : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleFunctionItemSave}
            validationSchema={
              initialMapValues?.isDatabaseScript
                ? validationSchemaDB
                : initialMapValues?.isMCPServer
                ? validationSchemaMCP
                : initialMapValues?.isHtmlFn
                ? validationSchemaHtml
                : initialMapValues?.isChatFn
                ? validationSchemaChat
                : initialMapValues?.isCodeTemplate
                ? validationSchemaCode
                : initialMapValues?.isAgentSkill
                ? validationSchemaAgentSkill
                : validationSchema
            }
            enableReinitialize
          >
            <FunctionSettingsAIView
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              initialMapValues={initialMapValues}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        initialMapValues,
        state.agentsFunctions,
        isEdit,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ],
    );
    return _view;
  },
);

export default FunctionSettingAI;
